import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useLanguage } from "@/context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROVIDERS = [
  { id: "gemini", label: "Gemini" },
  { id: "openai", label: "ChatGPT" },
];

const COPY = {
  it: {
    title: "RV Assistant",
    greeting: "Ciao! Sono l'assistente virtuale di RV Film. Posso aiutarti con servizi, tempi di produzione e budget indicativi, o guidarti verso un preventivo gratuito. Come posso aiutarti?",
    placeholder: "Scrivi un messaggio…",
    chips: ["Che servizi offrite?", "Quanto costa uno spot?", "Voglio un'analisi gratuita"],
    error: "Qualcosa è andato storto. Riprova o scrivici a info@rvfilm.it",
  },
  en: {
    title: "RV Assistant",
    greeting: "Hi! I'm RV Film's virtual assistant. I can help with services, production timelines and indicative budgets, or guide you to a free quote. How can I help?",
    placeholder: "Type a message…",
    chips: ["What services do you offer?", "How much does a commercial cost?", "I want a free analysis"],
    error: "Something went wrong. Please try again or email info@rvfilm.it",
  },
};

export const ChatWidget = () => {
  const { lang } = useLanguage();
  const t = COPY[lang] || COPY.it;
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [provider, setProvider] = useState("gemini");
  const [input, setInput] = useState("");
  const [sessionId] = useState(() => {
    let s = localStorage.getItem("rvfilm-chat-session");
    if (!s) {
      s = crypto.randomUUID();
      localStorage.setItem("rvfilm-chat-session", s);
    }
    return s;
  });
  const [messages, setMessages] = useState(() => [{ role: "assistant", text: (COPY[localStorage.getItem("rvfilm-lang") || "it"] || COPY.it).greeting }]);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const send = async (raw) => {
    const msg = (raw ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", text: msg }, { role: "assistant", text: "" }]);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: msg, lang, provider }),
      });
      if (!res.ok || !res.body) throw new Error("chat failed");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          const data = line.slice(5).trim();
          if (data === "[DONE]") continue;
          try {
            const j = JSON.parse(data);
            if (j.text) {
              setMessages((m) => {
                const next = [...m];
                next[next.length - 1] = { role: "assistant", text: next[next.length - 1].text + j.text };
                return next;
              });
            }
            if (j.error) throw new Error("stream error");
          } catch (e) {
            if (e.message === "stream error") throw e;
          }
        }
      }
    } catch {
      setMessages((m) => {
        const next = [...m];
        next[next.length - 1] = { role: "assistant", text: t.error };
        return next;
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="chat-toggle-button"
        aria-label={open ? "Chiudi chat" : "Apri chat"}
        style={{ bottom: "max(1.25rem, env(safe-area-inset-bottom))" }}
        className="fixed right-5 z-[96] w-14 h-14 rounded-full bg-red-600 hover:bg-red-500 transition-all duration-300 hover:scale-110 flex items-center justify-center shadow-lg shadow-red-600/30"
      >
        {open ? <X size={22} className="text-white" /> : <MessageCircle size={22} className="text-white" />}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="fixed bottom-24 right-5 z-[96] w-[calc(100vw-40px)] sm:w-96 h-[480px] max-h-[70vh] bg-[#101114] border border-white/10 rounded-xl shadow-2xl flex flex-col overflow-hidden"
            data-testid="chat-panel"
          >
            <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-white/10 bg-[#0B0B0E]">
              <div className="flex items-center gap-3">
                <span className="w-8 h-8 rounded-full bg-red-600 flex items-center justify-center shrink-0">
                  <Sparkles size={15} className="text-white" />
                </span>
                <p className="f-display font-bold text-sm tracking-tight">{t.title}</p>
              </div>
              <div className="flex gap-1" data-testid="chat-provider-toggle">
                {PROVIDERS.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => setProvider(p.id)}
                    data-testid={`chat-provider-${p.id}`}
                    className={`f-mono text-[9px] uppercase tracking-[0.15em] px-2.5 py-1.5 rounded-full border transition-colors ${
                      provider === p.id
                        ? "border-red-600 text-red-500"
                        : "border-white/10 text-slate-500 hover:text-slate-300"
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="chat-messages">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    data-testid={`chat-message-${i}`}
                    className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                      m.role === "user"
                        ? "bg-red-600 text-white rounded-br-sm"
                        : "bg-white/5 border border-white/10 text-slate-200 rounded-bl-sm"
                    }`}
                  >
                    {m.text || (busy && i === messages.length - 1 ? "…" : "")}
                  </div>
                </div>
              ))}
            </div>

            {messages.length <= 1 && (
              <div className="px-4 pb-2 flex flex-wrap gap-2">
                {t.chips.map((c) => (
                  <button
                    key={c}
                    onClick={() => send(c)}
                    data-testid={`chat-chip-${c.slice(0, 12)}`}
                    className="f-mono text-[10px] tracking-wide border border-white/15 hover:border-red-600/60 hover:text-red-400 transition-colors text-slate-300 px-3 py-1.5 rounded-full"
                  >
                    {c}
                  </button>
                ))}
              </div>
            )}

            <form
              onSubmit={(e) => {
                e.preventDefault();
                send();
              }}
              className="flex items-center gap-2 px-4 py-3 border-t border-white/10 bg-[#0B0B0E]"
            >
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={t.placeholder}
                data-testid="chat-input"
                className="flex-1 bg-transparent text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none"
              />
              <button
                type="submit"
                disabled={busy || !input.trim()}
                data-testid="chat-send-button"
                aria-label="Invia"
                className="w-9 h-9 rounded-full bg-red-600 hover:bg-red-500 disabled:opacity-40 transition-colors flex items-center justify-center shrink-0"
              >
                <Send size={15} className="text-white" />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
